import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";

import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";
import MasterLocationSelect from "components/MasterLocationSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { API_URL } from "config";
import { useAuth } from "../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";
import { creditNoteActionPermission, creditNoteFormFieldPermission } from "./creditNotePermissions";

const emptyAddress = {
  name: "",
  email: "",
  address: "",
  state: "",
  state_id: null,
  postal_code: "",
  country: "",
  country_id: null,
};
const addressFields = [
  ["name", "Name / Company"],
  ["email", "Email"],
  ["address", "Address"],
  ["state", "State"],
  ["postal_code", "Pincode"],
  ["country", "Country"],
];

const localToday = () => {
  const date = new Date();
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const round = (value, digits = 2) => Number(Number(value || 0).toFixed(digits));
const money = (value) => Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const optionLabel = (invoice) => {
  const remaining = money(invoice?.remaining_amount_usd);
  return `${invoice?.invoice_number || "Invoice"} — ${invoice?.client_name || "Client"} — $${remaining} remaining`;
};
const asOption = (invoice) => ({ ...invoice, value: invoice.id, label: optionLabel(invoice) });

const cellSx = {
  px: 0.55,
  py: 0.55,
  fontSize: "11px",
  "& .MuiInputBase-root": { minHeight: "34px", height: "34px", fontSize: "12px" },
};
const headSx = { px: 0.55, py: 0.7, fontSize: "11px", lineHeight: 1.2 };

function CreditNoteForm({ mode }) {
  const isEdit = mode === "edit";
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission } = useModulePermissions(permissions, userPermission, "credit_notes");
  const canField = (field) => hasModulePermission(creditNoteFormFieldPermission(mode, field));
  const canSave = hasModulePermission(creditNoteActionPermission(isEdit ? "update" : "create"));

  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Accept: "application/json",
  }), []);

  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    credit_note_date: localToday(),
    reference_number: "",
    notes: "",
    shipping_same_as_billing: true,
    billing: { ...emptyAddress },
    shipping: { ...emptyAddress },
  });
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, color: "success", message: "" });
  const [minimumDate, setMinimumDate] = useState("");
  const [maximumDate, setMaximumDate] = useState(localToday());

  const request = async (path, init = {}) => {
    const response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: { ...headers, ...(init.headers || {}) },
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      const validation = result.errors ? Object.values(result.errors).flat()[0] : null;
      throw new Error(validation || result.message || "Unable to process credit note request.");
    }
    return result;
  };

  const setSnapshotAddresses = (data, existing = null) => {
    const source = existing || data || {};
    setForm((current) => ({
      ...current,
      reference_number: source.reference_number ?? current.reference_number,
      notes: source.notes ?? current.notes,
      shipping_same_as_billing: Boolean(source.shipping_same_as_billing ?? true),
      billing: {
        name: source.billing_name || "",
        email: source.billing_email || "",
        address: source.billing_address || "",
        state: source.billing_state || "",
        state_id: source.billing_state_id || null,
        postal_code: source.billing_postal_code || "",
        country: source.billing_country || "",
        country_id: source.billing_country_id || null,
      },
      shipping: {
        name: source.shipping_name || "",
        email: source.shipping_email || "",
        address: source.shipping_address || "",
        state: source.shipping_state || "",
        state_id: source.shipping_state_id || null,
        postal_code: source.shipping_postal_code || "",
        country: source.shipping_country || "",
        country_id: source.shipping_country_id || null,
      },
    }));
  };

  const buildItems = (sourceItems, existingItems = null) => (sourceItems || [])
    .filter((item) => Number(item.remaining_total) > 0 && Number(item.remaining_quantity) > 0)
    .map((item) => {
      const existing = (existingItems || []).find(
        (row) => Number(row.source_invoice_item_id) === Number(item.source_invoice_item_id)
      );
      const rate = existing ? Number(existing.rate) : Number(item.rate || 0);
      const total = existing ? Number(existing.total) : Number(item.remaining_total || 0);
      const quantity = existing
        ? Number(existing.quantity)
        : Number(item.remaining_quantity || 0);
      return {
        ...item,
        description: existing?.description ?? item.description ?? "",
        quantity,
        rate,
        total: round(total),
      };
    });

  const applySourceDetails = (data, existing = null) => {
    const invoiceMap = new Map();
    [...(data.available_invoices || []), ...(data.source_invoices || [])].forEach((invoice) => {
      invoiceMap.set(Number(invoice.id), asOption(invoice));
    });
    const sourceOptions = (data.source_invoices || []).map((invoice) => invoiceMap.get(Number(invoice.id)));
    setOptions(Array.from(invoiceMap.values()));
    setSelected(sourceOptions.filter(Boolean));
    setClient(data.client || null);
    setCompany(data.invoicing_company || null);
    setSnapshot(data.snapshot || null);
    setSnapshotAddresses(data.snapshot, existing);
    setItems(buildItems(data.items, existing?.items));
  };

  const loadSourceDetails = async (invoiceIds, existing = null) => {
    if (!invoiceIds.length) return;
    setSourceLoading(true);
    setError("");
    try {
      const result = await request("/credit-notes/source-details", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invoice_ids: invoiceIds,
          ...(isEdit && id ? { credit_note_id: Number(id) } : {}),
        }),
      });
      applySourceDetails(result, existing);
    } catch (requestError) {
      setError(requestError.message);
      setSnackbar({ open: true, color: "error", message: requestError.message });
    } finally {
      setSourceLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const optionResult = await request(`/credit-notes/form-options?mode=${mode}`);
        if (!active) return;
        setOptions((optionResult.invoices || []).map(asOption));
        setMinimumDate(optionResult.minimum_credit_note_date || "");
        setMaximumDate(optionResult.maximum_credit_note_date || optionResult.today || localToday());
        if (isEdit) {
          const noteResult = await request(`/credit-notes/${id}?mode=edit`);
          if (!active) return;
          const note = noteResult.data || noteResult;
          setNumber(note.credit_note_number || note.invoice_number || "");
          setForm((current) => ({
            ...current,
            credit_note_date: note.credit_note_date || note.invoice_date || current.credit_note_date,
            reference_number: note.reference_number || "",
            notes: note.notes || "",
          }));
          await loadSourceDetails((note.source_invoice_ids || []).map(Number), note);
        }
      } catch (requestError) {
        if (!active) return;
        setError(requestError.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id, isEdit]);

  const chooseInvoices = async (values) => {
    const next = values || [];
    if (!next.length) {
      setSelected([]);
      setClient(null);
      setCompany(null);
      setSnapshot(null);
      setItems([]);
      setForm((current) => ({ ...current, billing: { ...emptyAddress }, shipping: { ...emptyAddress } }));
      try {
        const result = await request(`/credit-notes/form-options?mode=${mode}`);
        setOptions((result.invoices || []).map(asOption));
      } catch (requestError) {
        setError(requestError.message);
      }
      return;
    }
    await loadSourceDetails(next.map((invoice) => Number(invoice.value || invoice.id)));
  };

  const setAddress = (type, key, value) => {
    setForm((current) => ({ ...current, [type]: { ...current[type], [key]: value } }));
  };
  const toggleSame = (checked) => setForm((current) => ({
    ...current,
    shipping_same_as_billing: checked,
    shipping: checked ? { ...current.billing } : current.shipping,
  }));

  const setItem = (index, field, value) => {
    setItems((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const updated = { ...item, [field]: value };
      if (field === "quantity" || field === "rate") {
        updated.total = round(Number(updated.quantity || 0) * Number(updated.rate || 0));
      }
      return updated;
    }));
  };

  const sourceAmount = useMemo(() => round(items.reduce((sum, item) => sum + Number(item.total || 0), 0)), [items]);
  const totalQuantity = useMemo(() => round(items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)), [items]);
  const exchangeRate = String(snapshot?.currency_code || "USD").toUpperCase() === "USD"
    ? 1 : Number(snapshot?.exchange_rate || 1);
  const amount = round(sourceAmount * exchangeRate);
  const tax = snapshot?.tax_enabled ? round(amount * Number(snapshot?.tax_percentage || 0) / 100) : 0;
  const finalAmount = round(amount + tax);

  const submit = async () => {
    if (!selected.length || !items.length) {
      setSnackbar({ open: true, color: "error", message: "Select at least one source invoice and credit item." });
      return;
    }
    const invalid = items.find((item) => (
      Number(item.quantity) <= 0
      || Number(item.quantity) > Number(item.remaining_quantity) + 0.01
      || Number(item.total) <= 0
      || Number(item.total) > Number(item.remaining_total) + 0.01
    ));
    if (invalid) {
      setSnackbar({
        open: true,
        color: "error",
        message: "A credit quantity or amount is invalid or exceeds the remaining invoice item balance.",
      });
      return;
    }
    setSaving(true);
    setError("");
    try {
      const billing = form.billing;
      const shipping = form.shipping_same_as_billing ? billing : form.shipping;
      const payload = {
        invoice_ids: Array.from(new Set(items.map((item) => Number(item.source_invoice_id)))),
        credit_note_date: form.credit_note_date,
        reference_number: form.reference_number || null,
        notes: form.notes || null,
        billing_name: billing.name,
        billing_email: billing.email,
        billing_address: billing.address,
        billing_state: billing.state,
        billing_state_id: billing.state_id || null,
        billing_postal_code: billing.postal_code,
        billing_country: billing.country,
        billing_country_id: billing.country_id || null,
        shipping_same_as_billing: form.shipping_same_as_billing,
        shipping_name: shipping.name,
        shipping_email: shipping.email,
        shipping_address: shipping.address,
        shipping_state: shipping.state,
        shipping_state_id: shipping.state_id || null,
        shipping_postal_code: shipping.postal_code,
        shipping_country: shipping.country,
        shipping_country_id: shipping.country_id || null,
        items: items.map((item) => ({
          source_invoice_item_id: Number(item.source_invoice_item_id),
          description: item.description || null,
          quantity: Number(item.quantity),
          rate: Number(item.rate),
          total: Number(item.total),
        })),
      };
      const result = await request(isEdit ? `/credit-notes/${id}` : "/credit-notes", {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      navigate("/credit-notes", {
        replace: true,
        state: { message: result.message || `Credit note ${isEdit ? "updated" : "generated"} successfully.` },
      });
    } catch (requestError) {
      setError(requestError.message);
      setSnackbar({ open: true, color: "error", message: requestError.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={2}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <SoftTypography variant="h4" fontWeight="medium">
            {isEdit ? `Edit Credit Note${number ? ` - ${number}` : ""}` : "Generate Credit Note"}
          </SoftTypography>
          {hasModulePermission("credit_notes.pages.list.view") && (
            <SoftButton color="secondary" size="small" onClick={() => navigate("/credit-notes")}>
              <Icon>arrow_back</Icon>&nbsp; Credit Notes
            </SoftButton>
          )}
        </SoftBox>

        {loading ? (
          <SoftBox py={8} display="flex" justifyContent="center"><CircularProgress color="info" /></SoftBox>
        ) : (
          <Card sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "3fr 1fr" }} gap={1.25} mb={1.5}>
              {canField("source_invoice_numbers") && <SoftBox>
                <SoftTypography variant="caption">Source Invoice Numbers * (multiple allowed)</SoftTypography>
                <SoftSelect
                  placeholder="First select an invoice number"
                  options={options}
                  value={selected}
                  isMulti
                  isSearchable
                  isLoading={sourceLoading}
                  onChange={chooseInvoices}
                />
                {client && <SoftTypography variant="caption" display="block" mt={0.4}>
                  Only compatible invoices for {client.name} are shown after the first selection.
                </SoftTypography>}
              </SoftBox>}
              {canField("credit_note_date") && <SoftBox>
                <SoftTypography variant="caption">Credit Note Date *</SoftTypography>
                <SoftInput type="date" value={form.credit_note_date} inputProps={{ min: minimumDate, max: maximumDate }} onChange={(event) => setForm((current) => ({ ...current, credit_note_date: event.target.value }))} />
              </SoftBox>}
            </SoftBox>

            {(canField("client") || canField("credit_note_number") || canField("credit_note_month")) && (
              <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr 1fr" }} gap={1.25} mb={1.5}>
                {canField("client") && <SoftBox><SoftTypography variant="caption">Client (auto-selected)</SoftTypography><SoftInput value={client?.name || "Selected from invoice number"} disabled /></SoftBox>}
                {canField("credit_note_number") && <SoftBox><SoftTypography variant="caption">Credit Note Number</SoftTypography><SoftInput value={number || "Generated automatically"} disabled /></SoftBox>}
                {canField("credit_note_month") && <SoftBox><SoftTypography variant="caption">Credit Note Year-Month</SoftTypography><SoftInput value={(form.credit_note_date || "").slice(0, 7)} disabled /></SoftBox>}
              </SoftBox>
            )}

            {(company || snapshot) && <SoftBox display="flex" flexWrap="wrap" gap={2} mb={2} p={1.25} sx={{ backgroundColor: "#f8fafc", borderRadius: "8px" }}>
              {canField("invoicing_company") && <SoftTypography variant="button"><strong>Invoicing Company:</strong> {company?.name || "-"} ({company?.country || "-"})</SoftTypography>}
              {canField("billing_currency") && <SoftTypography variant="button"><strong>Billing Currency:</strong> {snapshot?.currency_code || "USD"}</SoftTypography>}
              {canField("exchange_rate") && <SoftTypography variant="button"><strong>Exchange Rate:</strong> {String(snapshot?.currency_code || "USD").toUpperCase() === "USD" ? "N/A" : `1 USD = ${snapshot?.exchange_rate}`}</SoftTypography>}
            </SoftBox>}

            <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
              <SoftBox>
                <SoftTypography variant="h6" mb={0.75}>Credit To Client</SoftTypography>
                {addressFields.filter(([key]) => !["state", "country"].includes(key) && canField(`billing_${key}`)).map(([key, label]) => <SoftBox key={key} mb={0.7}>
                  <SoftTypography variant="caption">{label}</SoftTypography>
                  <SoftInput value={form.billing[key] || ""} onChange={(event) => setAddress("billing", key, event.target.value)} />
                </SoftBox>)}
                <MasterLocationSelect
                  countryId={form.billing.country_id}
                  countryName={form.billing.country}
                  stateId={form.billing.state_id}
                  stateName={form.billing.state}
                  showCountry={canField("billing_country")}
                  showState={canField("billing_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    billing: {
                      ...current.billing,
                      country_id: countryId,
                      country: name,
                      state_id: null,
                      state: "",
                    },
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    billing: { ...current.billing, state_id: stateId, state: name },
                  }))}
                />
              </SoftBox>
              <SoftBox>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.4}>
                  <SoftTypography variant="h6">Shipping To Client</SoftTypography>
                  {canField("shipping_same_as_billing") && <FormControlLabel control={<Checkbox checked={form.shipping_same_as_billing} onChange={(event) => toggleSame(event.target.checked)} />} label="Same as Billing" />}
                </SoftBox>
                {addressFields.filter(([key]) => !["state", "country"].includes(key) && canField(`shipping_${key}`)).map(([key, label]) => <SoftBox key={key} mb={0.7}>
                  <SoftTypography variant="caption">{label}</SoftTypography>
                  <SoftInput value={(form.shipping_same_as_billing ? form.billing : form.shipping)[key] || ""} disabled={form.shipping_same_as_billing} onChange={(event) => setAddress("shipping", key, event.target.value)} />
                </SoftBox>)}
                <MasterLocationSelect
                  countryId={(form.shipping_same_as_billing ? form.billing : form.shipping).country_id}
                  countryName={(form.shipping_same_as_billing ? form.billing : form.shipping).country}
                  stateId={(form.shipping_same_as_billing ? form.billing : form.shipping).state_id}
                  stateName={(form.shipping_same_as_billing ? form.billing : form.shipping).state}
                  disabled={form.shipping_same_as_billing}
                  showCountry={canField("shipping_country")}
                  showState={canField("shipping_state")}
                  onCountryChange={({ id: countryId, name }) => setForm((current) => ({
                    ...current,
                    shipping: {
                      ...current.shipping,
                      country_id: countryId,
                      country: name,
                      state_id: null,
                      state: "",
                    },
                  }))}
                  onStateChange={({ id: stateId, name }) => setForm((current) => ({
                    ...current,
                    shipping: { ...current.shipping, state_id: stateId, state: name },
                  }))}
                />
              </SoftBox>
            </SoftBox>

            {canField("items_section") && <SoftBox mb={2}>
              <SoftTypography variant="h6" mb={0.75}>Source Invoice Credit Rows</SoftTypography>
              {sourceLoading ? <SoftBox py={3} display="flex" justifyContent="center"><CircularProgress size={24} color="info" /></SoftBox> : (
                <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 950, tableLayout: "fixed" }}>
                    <TableHead sx={{ display: "table-header-group" }}><TableRow>
                      {canField("item_source_invoice") && <TableCell sx={headSx}>Source Invoice</TableCell>}
                      {canField("item_project_code") && <TableCell sx={headSx}>Project Code</TableCell>}
                      {canField("item_country") && <TableCell sx={headSx}>Country</TableCell>}
                      {canField("item_description") && <TableCell sx={{ ...headSx, width: "25%" }}>Description</TableCell>}
                      {canField("item_quantity") && <TableCell sx={headSx}>Qty</TableCell>}
                      {canField("item_rate") && <TableCell sx={headSx}>Rate (USD)</TableCell>}
                      {canField("item_amount") && <TableCell sx={headSx}>Credit (USD)</TableCell>}
                      {canField("add_remove_items") && <TableCell sx={headSx}>Action</TableCell>}
                    </TableRow></TableHead>
                    <TableBody>
                      {items.map((item, index) => <TableRow key={item.source_invoice_item_id}>
                        {canField("item_source_invoice") && <TableCell sx={cellSx}>{item.source_invoice_number}</TableCell>}
                        {canField("item_project_code") && <TableCell sx={cellSx}>{item.project_code || "-"}</TableCell>}
                        {canField("item_country") && <TableCell sx={cellSx}>{item.country || "-"}</TableCell>}
                        {canField("item_description") && <TableCell sx={cellSx}><SoftInput value={item.description || ""} onChange={(event) => setItem(index, "description", event.target.value)} /></TableCell>}
                        {canField("item_quantity") && <TableCell sx={cellSx}><SoftInput type="number" value={item.quantity} inputProps={{ min: 0.01, max: Number(item.remaining_quantity || 0), step: 0.01 }} onChange={(event) => setItem(index, "quantity", event.target.value)} /></TableCell>}
                        {canField("item_rate") && <TableCell sx={cellSx}><SoftInput type="number" value={item.rate} inputProps={{ min: 0, step: 0.01 }} onChange={(event) => setItem(index, "rate", event.target.value)} /></TableCell>}
                        {canField("item_amount") && <TableCell sx={cellSx}>
                          <SoftInput type="number" value={item.total} inputProps={{ min: 0.01, max: item.remaining_total, step: 0.01 }} onChange={(event) => setItem(index, "total", event.target.value)} />
                          <SoftTypography variant="caption">Max ${money(item.remaining_total)}</SoftTypography>
                        </TableCell>}
                        {canField("add_remove_items") && <TableCell sx={cellSx} align="center"><IconButton color="error" size="small" onClick={() => setItems((current) => current.filter((_, row) => row !== index))}><Icon>delete</Icon></IconButton></TableCell>}
                      </TableRow>)}
                      {!items.length && <TableRow><TableCell colSpan={8} align="center">Select source invoice numbers to load credit rows.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              <SoftBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={3} mt={1}>
                {canField("total_quantity") && <SoftTypography variant="button" fontWeight="bold">Total Qty: {money(totalQuantity)}</SoftTypography>}
                {canField("usd_amount") && <SoftTypography variant="button" color="info" fontWeight="bold">Credit Amount: ${money(sourceAmount)} USD</SoftTypography>}
              </SoftBox>
            </SoftBox>}

            {(canField("tax") || canField("final_amount")) && <SoftBox display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 380px" }} gap={2} mb={2} p={1.5} sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}>
              <SoftBox>
                <SoftTypography variant="h6">Copied Invoice Currency & Tax Snapshot</SoftTypography>
                <SoftTypography variant="button" display="block">Currency, exchange rate and tax rule are copied from the selected source invoice(s).</SoftTypography>
                {canField("notes") && <SoftBox mt={1}><SoftTypography variant="caption">Notes</SoftTypography><SoftInput multiline rows={3} value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} /></SoftBox>}
              </SoftBox>
              <SoftBox display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="center">
                <SoftTypography variant="button" textAlign="right">USD Credit:</SoftTypography><SoftInput value={`$${money(sourceAmount)} USD`} disabled />
                <SoftTypography variant="button" textAlign="right">Amount:</SoftTypography><SoftInput value={`${snapshot?.currency_symbol || snapshot?.currency_code || "$"}${money(amount)}`} disabled />
                <SoftTypography variant="button" textAlign="right">Tax ({Number(snapshot?.tax_percentage || 0)}%):</SoftTypography><SoftInput value={`${snapshot?.currency_symbol || snapshot?.currency_code || "$"}${money(tax)}`} disabled />
                <SoftTypography variant="button" textAlign="right" fontWeight="bold">Final Credit:</SoftTypography><SoftInput value={`${snapshot?.currency_symbol || snapshot?.currency_code || "$"}${money(finalAmount)}`} disabled />
              </SoftBox>
            </SoftBox>}

            {error && <SoftTypography color="error" variant="button" display="block" mb={1}>{error}</SoftTypography>}
            {canSave && <SoftButton color="info" fullWidth disabled={saving || sourceLoading || !items.length || !form.credit_note_date} onClick={submit}>
              {saving ? "Saving..." : isEdit ? "Update Credit Note" : "Generate Credit Note"}
            </SoftButton>}
          </Card>
        )}
      </SoftBox>
      <Footer />
      <SoftSnackbar color={snackbar.color} icon={snackbar.color === "success" ? "check" : "warning"} title="Credit Note" content={snackbar.message} open={snackbar.open} onClose={() => setSnackbar((current) => ({ ...current, open: false }))} close={() => setSnackbar((current) => ({ ...current, open: false }))} />
    </DashboardLayout>
  );
}

CreditNoteForm.propTypes = { mode: PropTypes.oneOf(["create", "edit"]).isRequired };

export default CreditNoteForm;
